let pending = new Set();
let barriers = new Set();

export function trackSavePromise(promise) {
    const tracked = Promise.resolve(promise);
    pending.add(tracked);
    barriers.forEach(barrier => barrier.saves.add(tracked));
    tracked.then(
        () => pending.delete(tracked),
        () => pending.delete(tracked)
    );
    return tracked;
}

export function hasPendingSaves() {
    return pending.size > 0;
}

export function getPendingSaveCount() {
    return pending.size;
}

export function createPendingSaveBarrier() {
    const barrier = {
        saves: new Set(pending),
        observed: new Set(),
        results: [],
        closed: false
    };
    barriers.add(barrier);

    return {
        async wait() {
            while (true) {
                const current = Array.from(barrier.saves)
                    .filter(promise => !barrier.observed.has(promise));
                if (current.length === 0) {
                    break;
                }

                current.forEach(promise => barrier.observed.add(promise));
                barrier.results.push(...await Promise.all(current.map(promise => promise.then(
                    value => ({status: "fulfilled", value}),
                    reason => ({status: "rejected", reason})
                ))));
            }

            const failed = barrier.results.find(result => result.status === "rejected");
            if (failed) {
                throw failed.reason;
            }

            return barrier.results.map(result => result.value);
        },
        hasUnobservedSaves() {
            return barrier.saves.size > barrier.observed.size;
        },
        close() {
            if (!barrier.closed) {
                barrier.closed = true;
                barriers.delete(barrier);
            }
        }
    };
}

export async function waitForPendingSaves() {
    const barrier = createPendingSaveBarrier();
    try {
        return await barrier.wait();
    }
    finally {
        barrier.close();
    }
}

export function clearPendingSaves() {
    pending = new Set();
    barriers = new Set();
}
